// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { getDeposits, getBanks } from "../lib/api";
import { toast } from "sonner";
import { ArrowDownToLine, Search, ArrowRight } from "lucide-react";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Map stored deposit state-machine values to display status
const displayStatus = (state) => {
  if (state === 'completed') return 'success';
  if (['user_cancelled', 'clearing_failed', 'account_invalid', 'limit_exceeded', 'deposit_reversed'].includes(state)) return 'failed';
  return 'pending';
};

const StatusBadge = ({ status }) => {
  const statusStyles = {
    success: "status-success",
    failed: "status-error",
    pending: "status-warning"
  };
  const ds = displayStatus(status);

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusStyles[ds] || 'status-neutral'}`}>
      {ds}
    </span>
  );
};

const Deposits = () => {
  const [deposits, setDeposits] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterReceiving, setFilterReceiving] = useState("all");
  const [filterAcquirer, setFilterAcquirer] = useState("all");

  const fetchData = async () => {
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterReceiving !== "all") params.receiving_bank_code = filterReceiving;
      if (filterAcquirer !== "all") params.acquirer_bank_code = filterAcquirer;

      const [depRes, banksRes] = await Promise.all([
        getDeposits(params),
        getBanks()
      ]);
      setDeposits(depRes.data.deposits || []);
      setBanks(banksRes.data);
    } catch (error) {
      toast.error("Failed to load deposits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterReceiving, filterAcquirer]);

  const filteredDeposits = deposits.filter(d => {
    const query = searchQuery.toLowerCase();
    const ref = d.deposit_reference || '';
    const txId = d.id || '';
    const account = d.receiving_account_number || '';
    const name = `${d.depositor_name || ''} ${d.depositor_surname || ''}`;
    const msisdn = d.depositor_msisdn || '';

    return ref.toLowerCase().includes(query) ||
           txId.toLowerCase().includes(query) ||
           account.toLowerCase().includes(query) ||
           name.toLowerCase().includes(query) ||
           msisdn.toLowerCase().includes(query);
  });

  // Summary stats (only completed deposits count toward volume/fees)
  const completed = filteredDeposits.filter(d => displayStatus(d.status) === 'success');
  const totalVolume = completed.reduce((sum, d) => sum + (d.deposit_amount || 0), 0);
  const totalFees = completed.reduce((sum, d) => sum + (d.fee_applied || 0), 0);

  return (
    <div className="space-y-6" data-testid="deposits-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Deposits Entries</h1>
          <p className="text-muted-foreground">View all cross-bank ATM deposit entries</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Deposits</p>
            <p className="text-2xl font-bold font-mono mt-1">{filteredDeposits.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Completed</p>
            <p className="text-2xl font-bold font-mono mt-1 text-green-400">{completed.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Volume</p>
            <p className="text-2xl font-bold font-mono mt-1">{formatCurrency(totalVolume)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Fees</p>
            <p className="text-2xl font-bold font-mono mt-1 text-accent">{formatCurrency(totalFees)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search deposits..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-deposits-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status-select">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterReceiving} onValueChange={setFilterReceiving}>
              <SelectTrigger data-testid="filter-receiving-select">
                <SelectValue placeholder="Receiving Bank" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Receiving Banks</SelectItem>
                {banks.filter(b => b.bank_type === "issuer" || b.bank_type === "both").map(bank => (
                  <SelectItem key={bank.id} value={bank.bank_code}>{bank.bank_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterAcquirer} onValueChange={setFilterAcquirer}>
              <SelectTrigger data-testid="filter-acquirer-select">
                <SelectValue placeholder="ATM Bank" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ATM Banks</SelectItem>
                {banks.filter(b => b.bank_type === "acquirer" || b.bank_type === "both").map(bank => (
                  <SelectItem key={bank.id} value={bank.bank_code}>{bank.bank_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Deposits Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Deposit ID</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Flow</TableHead>
                <TableHead>Depositor</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Fee</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="animate-pulse text-muted-foreground">Loading deposits...</div>
                  </TableCell>
                </TableRow>
              ) : filteredDeposits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <ArrowDownToLine className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">No deposits found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDeposits.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <code className="font-mono text-xs text-muted-foreground">
                        {d.id.slice(0, 8)}...
                      </code>
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-sm bg-secondary px-2 py-1 rounded">
                        {(() => {
                          const ref = d.deposit_reference || d.atm_transaction_id || d.id;
                          return ref.length > 12 ? `${ref.slice(0, 12)}...` : ref;
                        })()}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <span className="text-primary">{d.acquirer_bank_code}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className="text-accent">{d.receiving_bank_code}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{d.depositor_name} {d.depositor_surname}</div>
                        <div className="text-xs text-muted-foreground font-mono">{d.depositor_msisdn}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-xs text-muted-foreground">
                        {d.receiving_account_number}
                      </code>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono font-medium">{formatCurrency(d.deposit_amount)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-accent">{formatCurrency(d.fee_applied)}</span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={d.status} />
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(d.created_at)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Deposits;
